import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { BwjImageBrowser, IBrowserImage } from '../../../../framework/components/bwj-image-browser';
import { DoorRecommendation } from '../../../../entities-generated/common/door-recommendation';
import { noWrapStyle } from '../../../../framework/utils/no-wrap-style';
import { BwjPalette } from '../../../../app-core/app-theme';

interface IProductImageBrowserProps {
  id: string;
  idPrefix: string;
  images: IBrowserImage[];
  imageWidth: number;
  recommendation?: DoorRecommendation | undefined;
}

const getStyleClasses = (theme: ITheme) => {
  const root: IStyle = {
    position: 'relative',
  };

  const tag: IStyle = {
    ...noWrapStyle as any,
    position: 'absolute',
    top: '0.75rem',
    left: '0.75rem',
    margin: 0,
    padding: '0.3rem 0.6rem',
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    color: theme.palette.white,
    fontWeight: 600,
    zIndex: 2,
    borderRadius: 3,
    maxWidth: '50%',
  };

  return mergeStyleSets({
    root,
    tag,
  });
}

const getTagProps = (recommendation: DoorRecommendation | undefined) => {
  if(recommendation === undefined) { return undefined; }
  if(recommendation === DoorRecommendation.CustomerSelection) {
    return {
      color: BwjPalette.themeSuccess,
      alt: 'This is your current selection for this entryway',
      label: 'Current Selection',
    };
  }
  if(recommendation === DoorRecommendation.ExactMatch) {
    return {
      color: BwjPalette.themeInfo,
      alt: 'You chose this exact door for a different entryway',
      label: 'Exact Match!',
    };
  }
  if(recommendation === DoorRecommendation.CloseFamilyMember) {
    return {
      color: BwjPalette.themeInfo,
      alt: 'This door closely matches at least one of your previous selections',
      label: 'Close Match',
    };
  }
  if(recommendation === DoorRecommendation.DistantRelative) {
    return {
      color: BwjPalette.themeInfo,
      alt: 'This door is recommended based on your previous selections',
      label: 'Recommended',
    };
  }

  return undefined;
}

export const ProductImageBrowser: React.FunctionComponent<IProductImageBrowserProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const { images } = props;
  const [displayIndex, setDisplayIndex] = useState(0);
  const tagProps = getTagProps(props.recommendation);

  useEffect(() => {
    if(!!images) { // always true
      setDisplayIndex(0);
    }
  }, [images]);
  
  return (
    <div
      className={cs.root}
      style={{ width: props.imageWidth }}
    >
      <BwjImageBrowser
        id={`${props.idPrefix}-imgset-${props.id}`}
        images={images.length > 0 ? images : [{ src: '' }]}
        displayIndex={displayIndex}
        loadImages={images.length > 0}
        imageWidth={props.imageWidth}
        onDisplayIndexChanged={index => setDisplayIndex(index)}
        widthToHeightRatio={1}
      />
      {!!tagProps &&
      <p
        className={cs.tag}
        style={{ backgroundColor: tagProps.color }}
        title={tagProps.alt}
        aria-label={tagProps.alt}
      >
        {tagProps.label}
      </p>}
    </div>
  );
};
